import { RxPlusCircled } from '@react-icons/all-files/rx/RxPlusCircled'
import { useTranslation } from '@disco/i18n/use-translation'
import cuid from 'cuid'
import React, { useCallback, useMemo } from 'react'

import type {
  PublishingSplitData,
  SplitData,
  SplitsKey,
} from '@disco/state-management/reducers/rightsManagement'
import {
  addSplit,
  clearSplitValidationErrors,
  deletePublishingSplit,
  getBulkSaveError,
  getFailedBulkSaveResults,
  getSplitsValidation,
  getTotalPublishingSplitPercentage,
  getTrackError,
  getTrackLevelErrors,
  getTrackPublishingSplits,
  getTrackValidationErrors,
  setSplitFieldError,
  updateSplit,
} from '@disco/state-management/reducers/rightsManagement'

import type { RightsHolderEntity } from 'types/rights-data'
import type { EditableTrack, TrackId } from 'types/track'

import {
  Box,
  Button,
  DiscoCallout,
  Flex,
  Heading,
  Table,
  Text,
  TextField,
} from 'components/ds'

import { useDispatch, useSelector } from 'hooks/use-redux'

import { PublishingRow } from './publishing-row'

import styles from '../styles.module.css'
import { RxInfoCircled } from '@react-icons/all-files/rx/RxInfoCircled'
import { checkPublishingSplits } from './validation-errors'
import Tooltip from 'components/ui/tooltip'
import InfoPopover from '../../info-popover'
import { validateIswc } from 'features/rights-data/utils/identifier-validation'

interface PublishingTabProps {
  editedTrack: EditableTrack & {
    trackIds?: TrackId[]
  }
  updateEditedTrack: (track: any) => void
  /** Whether we're in bulk editing mode (multiple tracks selected) */
  isBulkMode: boolean
  /** The key used to identify this track/bulk in Redux state */
  splitsKey: SplitsKey
  /** Whether a save has been attempted (used to show validation errors only after save attempt) */
  saveAttempted?: boolean
  /** All rights holders */
  allRightsHolders: RightsHolderEntity[]
  /** Whether rights holders data is still loading */
  isRightsHoldersLoading: boolean
  backendErrorMessage: string
  backendErrorTab: null | 'recording' | 'publishing'
  /** Whether the Recording tab is enabled for this business */
  recordingTabEnabled: boolean
  /** Disables all form inputs (used while a save is in flight) */
  disabled?: boolean
  /** Disables only the splits rows and Add action (e.g. until rights load) */
  splitsDisabled?: boolean
}

/**
 * PublishingTab - Publishing rights table with Writer/Publisher/PRO/Split columns
 *
 * This component manages the table UI for publishing rights.
 * Save handlers and shared UI elements (FormNote, Footer) are managed by the parent component.
 */
export function PublishingTab({
  editedTrack,
  updateEditedTrack,
  isBulkMode,
  splitsKey,
  saveAttempted = false,
  allRightsHolders,
  isRightsHoldersLoading,
  backendErrorMessage,
  backendErrorTab,
  recordingTabEnabled,
  disabled = false,
  splitsDisabled = false,
}: PublishingTabProps) {
  const dispatch = useDispatch()
  const { t } = useTranslation('rights')

  // Filter rights holders by entity type for publishing tab
  const { writers, publishers, pros } = useMemo(() => {
    const result = {
      writers: [] as RightsHolderEntity[],
      publishers: [] as RightsHolderEntity[],
      pros: [] as RightsHolderEntity[],
    }

    for (const entity of allRightsHolders) {
      switch (entity.entity_type) {
        case 'Writer':
          result.writers.push(entity)
          break
        case 'Publisher':
          result.publishers.push(entity)
          break
        case 'PRO':
          result.pros.push(entity)
          break
      }
    }

    return result
  }, [allRightsHolders])

  // Get data from Redux
  const splits = useSelector(state =>
    getTrackPublishingSplits(state, splitsKey),
  )
  const totalSplitPercentage = useSelector(state =>
    getTotalPublishingSplitPercentage(state, splitsKey),
  )
  const trackError = useSelector(state => getTrackError(state, splitsKey))
  const trackLevelErrors = useSelector(state =>
    getTrackLevelErrors(state, splitsKey),
  )
  const splitsValidation = useSelector(state =>
    getSplitsValidation(state, splitsKey, recordingTabEnabled),
  )
  const validationErrors = useSelector(state =>
    getTrackValidationErrors(state, splitsKey),
  )

  const bulkSaveError = useSelector(getBulkSaveError)
  const failedBulkSaveResults = useSelector(getFailedBulkSaveResults)

  const handleAddWriter = useCallback(() => {
    const newSplit: PublishingSplitData = {
      id: cuid(),
      kind: 'publishing',
      writerId: null,
      publisherId: null,
      adminId: null,
      proId: null,
      split: null,
      isNew: true,
    }

    dispatch(addSplit({ trackId: splitsKey, splitData: newSplit }))
  }, [dispatch, splitsKey])

  const handleUpdateSplit = useCallback(
    (splitId: number | string, changes: Partial<Omit<SplitData, 'id'>>) => {
      const finalChanges = { ...changes }
      const isWriterUpdate = Object.hasOwn(changes, 'writerId')

      if (isWriterUpdate) {
        const writer = writers.find(
          w => w.id === (changes as PublishingSplitData).writerId,
        )
        if (writer) {
          const splitBeingEdited: PublishingSplitData | undefined = splits.find(
            s => s.id === splitId,
          )
          if (splitBeingEdited) {
            const hasEntityDetails =
              Object.hasOwn(writer, 'entity_details') &&
              writer.entity_details !== null

            if (hasEntityDetails) {
              const writerHasDefaultPro =
                hasEntityDetails &&
                Object.hasOwn(
                  writer.entity_details as Record<string, any>,
                  'default_pro_id',
                )
              const writerHasDefaultPublisher =
                hasEntityDetails &&
                Object.hasOwn(
                  writer.entity_details as Record<string, any>,
                  'default_publisher_id',
                )
              const splitHasNoPro = splitBeingEdited.proId === null
              const splitHasNoPublisher = splitBeingEdited.publisherId === null
              if (writerHasDefaultPro && splitHasNoPro) {
                ;(finalChanges as PublishingSplitData).proId = writer
                  .entity_details?.default_pro_id as number
              }
              if (writerHasDefaultPublisher && splitHasNoPublisher) {
                ;(finalChanges as PublishingSplitData).publisherId = writer
                  .entity_details?.default_publisher_id as number
              }
            }
          }
        }
      }
      dispatch(
        updateSplit({ trackId: splitsKey, splitId, changes: finalChanges }),
      )
    },
    [dispatch, splitsKey, writers, splits],
  )

  const handleDeleteSplit = useCallback(
    (splitId: number | string) => {
      dispatch(deletePublishingSplit({ trackId: splitsKey, splitId }))
    },
    [dispatch, splitsKey],
  )

  const handleValidate = useCallback(
    (splitId: number | string, field: string, message: string) => {
      dispatch(
        setSplitFieldError({ trackId: splitsKey, splitId, field, message }),
      )
    },
    [dispatch, splitsKey],
  )

  const handleClearValidation = useCallback(
    (splitId: number | string) => {
      dispatch(clearSplitValidationErrors({ trackId: splitsKey, splitId }))
    },
    [dispatch, splitsKey],
  )

  const handleProNumberChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!editedTrack || !editedTrack.metadata) {
        return
      }
      const metadata = editedTrack.metadata
      updateEditedTrack({
        ...editedTrack,
        metadata: {
          ...metadata,
          ProNumber: {
            ...metadata.ProNumber,
            mixed: false,
            value: e.target.value,
          },
        },
      })
    },
    [editedTrack, updateEditedTrack],
  )

  const handleIswcChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!editedTrack || !editedTrack.metadata) {
        return
      }

      const metadata = editedTrack.metadata
      updateEditedTrack({
        ...editedTrack,
        metadata: {
          ...metadata,
          ISWC: {
            ...metadata.ISWC,
            mixed: false,
            value: e.target.value,
          },
        },
      })
    },
    [editedTrack, updateEditedTrack],
  )

  const errorMessage = checkPublishingSplits(splits, {
    isBulkMode,
    splitsValidation,
    totalSplitPercentage,
  })

  const proNumberMeta = editedTrack.metadata?.ProNumber
  const proNumberValue = proNumberMeta?.value ?? ''
  const proNumberIsMixed = proNumberMeta?.mixed ?? false

  const iswcMeta = editedTrack.metadata?.ISWC
  const iswcValue = iswcMeta?.value ?? ''
  const iswcIsMixed = iswcMeta?.mixed ?? false

  const { valid: isIswcValid } = iswcIsMixed
    ? { valid: true }
    : validateIswc(iswcValue)

  let hasEmptyWriters = false
  if (Object.hasOwn(splitsValidation.errors, 'hasEmptyWriters')) {
    hasEmptyWriters = splitsValidation.errors['hasEmptyWriters']
  }

  return (
    <div className={styles.container}>
      <Flex mb="1" gap="2">
        <Box flexGrow="1" flexShrink="1" flexBasis="0%">
          <Flex align="center" gap="1" mb="2">
            <Text size="2" weight="medium">
              PRO Number
            </Text>
            <InfoPopover
              label={t('rights:moreInfoAbout', 'More info about {{field}}', {
                field: 'PRO Number',
              })}
              title={t(
                'rights:proNumberInfoTitle',
                'Performing Rights Organization (PRO) Number',
              )}
            >
              {t(
                'rights:proNumberInfoBody',
                'This is a general field where you can enter the work number for your collection society e.g. PRO work number. Please note that this information is not written to the downloaded metadata of the track.',
              )}
            </InfoPopover>
          </Flex>
          <Box>
            <TextField.Root
              value={proNumberIsMixed ? '' : proNumberValue}
              onChange={handleProNumberChange}
              disabled={disabled}
              placeholder={proNumberIsMixed ? 'Mixed' : 'Enter PRO number'}
            />
          </Box>
        </Box>
        <Box flexGrow="1" flexShrink="1" flexBasis="0%">
          <Flex align="center" gap="1" mb="2">
            <Text size="2" weight="medium">
              ISWC
            </Text>
            <InfoPopover
              label={t('rights:moreInfoAbout', 'More info about {{field}}', {
                field: 'ISWC',
              })}
              title={t(
                'rights:iswcInfoTitle',
                'International Standard Musical Work Code (ISWC)',
              )}
            >
              {t(
                'rights:iswcInfoBody',
                'Enter a valid ISWC. Please note that this information is not written to the downloaded metadata of the track.',
              )}
            </InfoPopover>
          </Flex>
          <Box className={isIswcValid ? undefined : styles.fieldWithError}>
            <TextField.Root
              value={iswcIsMixed ? '' : iswcValue}
              onChange={handleIswcChange}
              disabled={disabled}
              placeholder={iswcIsMixed ? 'Mixed' : 'Enter ISWC'}
              // color={!isIswcValid ? 'red' : undefined}
            >
              <TextField.Slot side="right">
                {!isIswcValid && (
                  <Tooltip
                    content="This doesn't appear to be a valid ISWC."
                    position="top"
                  >
                    <Flex>
                      <RxInfoCircled color="red" size={14} />
                    </Flex>
                  </Tooltip>
                )}
              </TextField.Slot>
            </TextField.Root>
          </Box>
        </Box>
      </Flex>

      <Box mb="4">
        <Flex justify="between" align="center" mb="1">
          <Heading size="4">Writers</Heading>
          <Button
            variant="soft"
            color="gray"
            size="2"
            onClick={handleAddWriter}
            disabled={splitsDisabled}
          >
            <RxPlusCircled />
            Add
          </Button>
        </Flex>

        <Table.Root variant="surface" size="1" layout="fixed">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeaderCell width="25%" minWidth="140px">
                <Text size="2" weight="medium">
                  Writer <span className={styles.required}>*</span>
                </Text>
              </Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell width="25%" minWidth="140px">
                <Text size="2" weight="medium">
                  Publisher
                </Text>
              </Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell width="25%" minWidth="120px">
                <Text size="2" weight="medium">
                  PRO
                </Text>
              </Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell width="140px" minWidth="140px">
                <Text
                  size="2"
                  weight="medium"
                  className={styles.splitColumnHeader}
                  title={`Split (${totalSplitPercentage}%)`}
                >
                  Split ({totalSplitPercentage}%)
                </Text>
              </Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell width="5%" justify="center" />
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {splits.map(split => {
              const splitValidationError = validationErrors.find(
                error => error.splitId === split.id,
              )
              return (
                <PublishingRow
                  key={split.id}
                  split={split}
                  splitsKey={splitsKey}
                  validationError={splitValidationError}
                  writers={writers}
                  publishers={publishers}
                  pros={pros}
                  isRightsHoldersLoading={isRightsHoldersLoading}
                  onUpdate={handleUpdateSplit}
                  onDelete={handleDeleteSplit}
                  onValidate={handleValidate}
                  onClearValidation={handleClearValidation}
                  disabled={splitsDisabled}
                />
              )
            })}
          </Table.Body>
        </Table.Root>
      </Box>

      {isBulkMode && (
        <Box mb="4">
          <DiscoCallout type="info">
            You are editing {editedTrack.trackIds?.length} tracks. Writers added
            here will be applied to all selected tracks.
          </DiscoCallout>
        </Box>
      )}

      {isBulkMode && bulkSaveError && failedBulkSaveResults.length === 0 && (
        <Box mb="4">
          <DiscoCallout variant="surface" type="error">
            {bulkSaveError}
          </DiscoCallout>
        </Box>
      )}

      {errorMessage && (
        <Box mb="4">
          <DiscoCallout
            variant="surface"
            type={totalSplitPercentage > 100 ? 'error' : 'warning-important'}
          >
            {errorMessage}
          </DiscoCallout>
        </Box>
      )}

      {saveAttempted && hasEmptyWriters && (
        <Box mb="4">
          <DiscoCallout variant="surface" type="error">
            Each row with data must have a writer selected.
          </DiscoCallout>
        </Box>
      )}

      {trackLevelErrors.length > 0 && (
        <Box mb="4">
          <DiscoCallout variant="surface" type="error">
            {trackLevelErrors.map((error, index) => (
              <Box
                key={`${error.trackId}-${index}`}
                mb={index < trackLevelErrors.length - 1 ? '2' : undefined}
              >
                {error.message}
              </Box>
            ))}
          </DiscoCallout>
        </Box>
      )}

      {trackError && trackLevelErrors.length === 0 && (
        <Box mb="4">
          <DiscoCallout variant="surface" type="error">
            {trackError}
          </DiscoCallout>
        </Box>
      )}

      {backendErrorMessage !== '' && (
        <Box mb="4">
          <DiscoCallout type="error">
            {backendErrorTab === 'recording'
              ? 'We were unable to save your changes. Please resolve the error on the Recording rights tab and try again.'
              : backendErrorMessage}
          </DiscoCallout>
        </Box>
      )}
    </div>
  )
}

export default PublishingTab
